import { useCallback, useEffect, type ReactNode } from 'react'
import { Coins } from 'lucide-react'
import { usePricingEdition } from '@renderer/chat/hooks/use-plan-features'
import { LockedPage, LockNotice, PlanUpgradeButton } from '@renderer/components/pricing/locked-page'
import { creditsOutCopy, type LimitCopy } from '@renderer/components/pricing/limits-copy'
import { AmplitudeRenderer } from '@renderer/utils/amplitude.renderer'
import { isCreditsOutBanner, limitCopyContext, resolvePlanBanners, type PlanBanner } from './plan-banner-rules'
import { openSubscriptionSettings, planBannerRoute, usePlanBannerInputs } from './sidepanel-plan-banners'

export interface CreditsLock {
  banner: PlanBanner
  /** The single-limit copy: the page in view is about credits, whatever else is spent. */
  copy: LimitCopy
}

/**
 * Out of credits on the revamp, drafting and asking Highlight both stop, so the
 * surfaces that spend credits lock on the same verdict as the banner. Unlike
 * the banner it can't be dismissed: the limit holds either way.
 */
export function useCreditsLock(): CreditsLock | null {
  const edition = usePricingEdition()
  const inputs = usePlanBannerInputs()
  if (edition !== 'v2-revamp') return null
  const banner = resolvePlanBanners(inputs).find(isCreditsOutBanner)
  if (!banner) return null
  return { banner, copy: creditsOutCopy(limitCopyContext(banner, inputs)) }
}

interface CreditsLockedPageProps {
  lock: CreditsLock
  /** Analytics surface. */
  from: string
  /** The page as it would be, drawn under the blur. */
  children: ReactNode
}

/** A page held back by spent credits: the page blurred, and the credit limit with its one way on. */
export function CreditsLockedPage({ lock, from, children }: CreditsLockedPageProps) {
  const { banner, copy } = lock
  const { action, ctaPlan } = banner

  useEffect(() => {
    AmplitudeRenderer.trackEvent('Paywall', {
      action: 'shown',
      feature: 'credits-out',
      reason: action,
      plan: ctaPlan,
      from,
    })
  }, [action, ctaPlan, from])

  const onAction = useCallback(() => {
    AmplitudeRenderer.trackEvent('Paywall', {
      action: 'clicked',
      feature: 'credits-out',
      reason: action,
      plan: ctaPlan,
      from,
    })
    openSubscriptionSettings(planBannerRoute(action, 'credits'))
  }, [action, ctaPlan, from])

  return (
    <LockedPage
      notice={
        <LockNotice
          icon={<Coins className="size-5" />}
          plan={ctaPlan}
          title={copy.title}
          description={copy.body}
          actions={
            <PlanUpgradeButton plan={ctaPlan} onClick={onAction}>
              {copy.cta}
            </PlanUpgradeButton>
          }
        />
      }
    >
      {children}
    </LockedPage>
  )
}
